// src/components/dependants/sharedNote.component.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import DragBox2026 from "../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import { getMyScouts, getUserFromToken } from "../../../utils/functions.ts";
import SharedNoteEditor from "./sharedNoteEditor.component.jsx";

const getNoteId = (note) => String(note?.id || note?.ID || note?.noteId || "");
const getScoutId = (scout) => String(scout?.userId || scout?.scout || scout?.id || "").trim();
const getScoutName = (scout) => String(scout?.displayName || scout?.name || getScoutId(scout) || "").trim();
const getSharedScoutIds = (note) => String(note?.sharedTo || note?.SharedTo || "")
  .split(",")
  .map((id) => id.trim())
  .filter(Boolean);
const getLastSharedNoteStorageKey = (user) => `FBX_LAST_SHARED_NOTE_${user?.team || ""}_${user?.scout || user?.userId || ""}`;
const SHARED_NOTE_CHANGED_EVENT = "FBX_SHARED_NOTE_CHANGED_COUNT";
const isEveryoneShared = (note) => {
  const scope = String(note?.shareScope || note?.ShareScope || "team").trim().toLowerCase();
  return !scope || ["team", "all", "everyone"].includes(scope);
};

const SharedNotes = () => {
  const notesEndpoint = "https://www.fbxchange.com/ReactPHP/notes.php";
  const pollMs = 4000;
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState(null);
  const [selectedNoteId, setSelectedNoteId] = useState("");
  const [isDirty, setIsDirty] = useState(false);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [team, setTeam] = useState("");
  const [scouts, setScouts] = useState([]);
  const [showCreateNote, setShowCreateNote] = useState(false);
  const [newNoteTitle, setNewNoteTitle] = useState("");
  const [shareWithEveryone, setShareWithEveryone] = useState(true);
  const [selectedScoutIds, setSelectedScoutIds] = useState([]);
  const [creatingNote, setCreatingNote] = useState(false);
  const [createError, setCreateError] = useState("");
  const [showShareEditor, setShowShareEditor] = useState(false);
  const [editShareWithEveryone, setEditShareWithEveryone] = useState(true);
  const [editShareScoutIds, setEditShareScoutIds] = useState([]);
  const [savingShare, setSavingShare] = useState(false);
  const [shareError, setShareError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [deletingNote, setDeletingNote] = useState(false);
  const [confirmDeleteNoteId, setConfirmDeleteNoteId] = useState("");
  const [notesMenuOpen, setNotesMenuOpen] = useState(false);
  const [changedNoteIds, setChangedNoteIds] = useState([]);
  const userRef = useRef(null);
  const notesMenuRef = useRef(null);
  const selectedNoteIdRef = useRef("");
  const isDirtyRef = useRef(false);
  const lastSeenNoteIdRef = useRef("");
  const lastSavedBy = note?.lastEditBy || note?.updatedBy || "Unknown";
  const lastSavedAt = note?.lastUpdated || note?.updatedAt || "";
  const currentScoutId = String(userRef.current?.scout || userRef.current?.userId || "").trim();
  const otherScouts = scouts.filter((scout) => getScoutId(scout) && getScoutId(scout) !== currentScoutId);
  const isMainNote = note?.isMainNote === true || note?.isMainNote === 1 || note?.isMainNote === "1";
  const noteCreatorId = String(note?.createdByScoutId || note?.CreatedByScoutId || "").trim();
  const canEditNoteSharing = !isMainNote && noteCreatorId !== "" && noteCreatorId === currentScoutId;
  const canDeleteNote = canEditNoteSharing;
  const noteSharedWithEveryone = isMainNote || isEveryoneShared(note);
  const noteSharedScoutIds = getSharedScoutIds(note);
  const isNoteMain = (sourceNote) => sourceNote?.isMainNote === true || sourceNote?.isMainNote === 1 || sourceNote?.isMainNote === "1";
  const getNoteCreatorId = (sourceNote) => String(sourceNote?.createdByScoutId || sourceNote?.CreatedByScoutId || "").trim();
  const getNoteTitle = (sourceNote) => sourceNote?.title || (isNoteMain(sourceNote) ? "Shared Note" : "Untitled Note");
  const getNoteCreatorName = (sourceNote) => {
    const creatorName = String(sourceNote?.createdBy || sourceNote?.CreatedBy || sourceNote?.createdByScoutName || "").trim();
    if (creatorName) return creatorName;

    const creatorId = getNoteCreatorId(sourceNote);
    return getScoutName(scouts.find((scout) => getScoutId(scout) === creatorId));
  };
  const getNoteMenuTitle = (sourceNote, showCreator = false) => {
    const title = getNoteTitle(sourceNote);
    const creatorName = showCreator ? getNoteCreatorName(sourceNote) : "";
    return creatorName ? `${title} [${creatorName}]` : title;
  };
  const getNoteShareCount = (sourceNote) => {
    if (isEveryoneShared(sourceNote)) return otherScouts.length;
    return new Set(getSharedScoutIds(sourceNote).filter((id) => id !== currentScoutId)).size;
  };
  const noteIsSharedWithAnyone = (sourceNote) => getNoteShareCount(sourceNote) > 0;
  const defaultSharedNote = notes.find(isNoteMain) || notes[0] || null;
  const defaultSharedNoteId = getNoteId(defaultSharedNote);
  const myNotes = notes.filter((item) => getNoteId(item) !== defaultSharedNoteId && !isNoteMain(item) && getNoteCreatorId(item) === currentScoutId);
  const sharedNotes = notes.filter((item) => getNoteId(item) !== defaultSharedNoteId && !isNoteMain(item) && getNoteCreatorId(item) !== currentScoutId);
  const selectedNoteTitle = note ? getNoteMenuTitle(note, !isNoteMain(note) && getNoteCreatorId(note) !== currentScoutId) : "Choose Note";
  const changedNoteIdSet = new Set(changedNoteIds.map(String));
  const changedNoteCount = changedNoteIds.length;
  const getSharedPillLabels = () => {
    if (!note) return [];
    if (noteSharedWithEveryone) return ["Shared Team Wide"];

    const creatorId = getNoteCreatorId(note);
    const ids = [...new Set(noteSharedScoutIds.filter(Boolean).filter((id) => id !== creatorId))];
    return ids.map((id) => getScoutName(scouts.find((scout) => getScoutId(scout) === id)) || `#${id}`);
  };

  useEffect(() => {
    selectedNoteIdRef.current = selectedNoteId;
  }, [selectedNoteId]);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    if (!notesMenuOpen) return undefined;

    const closeOnOutsideClick = (event) => {
      if (notesMenuRef.current && !notesMenuRef.current.contains(event.target)) {
        setNotesMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [notesMenuOpen]);

  useEffect(() => {
    setShowShareEditor(false);
    setRefreshRequired(false);
    setShareError("");
    setDeleteError("");
    setConfirmDeleteNoteId("");
    setEditShareWithEveryone(noteSharedWithEveryone);
    setEditShareScoutIds(noteSharedScoutIds.filter((id) => id !== currentScoutId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getNoteId(note), currentScoutId]);

  const getNoteVersion = (nextNote) => [
    nextNote?.id || "",
    nextNote?.lastUpdated || nextNote?.updatedAt || "",
    nextNote?.lastEditBy || "",
    typeof nextNote?.noteJson === "string" ? nextNote.noteJson : JSON.stringify(nextNote?.noteJson || ""),
  ].join("|");

  const saveLastSharedNoteId = useCallback((noteId) => {
    const user = userRef.current;
    if (!user || !noteId) return;
    try {
      window.localStorage.setItem(getLastSharedNoteStorageKey(user), String(noteId));
    } catch {}
  }, []);

  const loadLastSharedNoteId = useCallback(() => {
    const user = userRef.current;
    if (!user) return "";
    try {
      return window.localStorage.getItem(getLastSharedNoteStorageKey(user)) || "";
    } catch {
      return "";
    }
  }, []);

  const publishChangedCount = useCallback((noteIds = []) => {
    const cleanIds = [...new Set((Array.isArray(noteIds) ? noteIds : []).map(String).filter(Boolean))];
    setChangedNoteIds(cleanIds);
    try {
      window.dispatchEvent(new CustomEvent(SHARED_NOTE_CHANGED_EVENT, {
        detail: { changedCount: cleanIds.length, changedNoteIds: cleanIds },
      }));
    } catch {}
  }, []);

  const loadScouts = useCallback(async (user) => {
    if (!user?.scout || !user?.team) return;
    const scoutList = await getMyScouts(user.scout, user.team);
    const cleanScouts = Array.isArray(scoutList) ? scoutList : [];
    setScouts(cleanScouts);
    setSelectedScoutIds((current) => current.length > 0 ? current : cleanScouts.map(getScoutId).filter(Boolean));
  }, []);

  const applyIncomingNotes = useCallback((incomingNotes = [], incomingChangedIds = null) => {
    const cleanNotes = Array.isArray(incomingNotes) ? incomingNotes : [];
    setNotes(cleanNotes);
    publishChangedCount(Array.isArray(incomingChangedIds)
      ? incomingChangedIds
      : cleanNotes.filter((item) => item?.hasUnreadChanges).map(getNoteId));

    const currentSelectedId = selectedNoteIdRef.current;
    const savedSelectedId = loadLastSharedNoteId();
    const nextSelectedId = cleanNotes.some((item) => getNoteId(item) === currentSelectedId)
      ? currentSelectedId
      : cleanNotes.some((item) => getNoteId(item) === savedSelectedId)
        ? savedSelectedId
        : getNoteId(cleanNotes[0]);

    if (nextSelectedId && nextSelectedId !== currentSelectedId) {
      setSelectedNoteId(nextSelectedId);
      saveLastSharedNoteId(nextSelectedId);
    }

    const incomingNote = cleanNotes.find((item) => getNoteId(item) === nextSelectedId) || cleanNotes[0] || null;
    if (!isDirtyRef.current) {
      setNote((current) => getNoteVersion(current) === getNoteVersion(incomingNote) ? current : incomingNote);
    }
  }, [loadLastSharedNoteId, publishChangedCount, saveLastSharedNoteId]);

  const fetchLatest = useCallback(async ({ showLoading = false } = {}) => {
    let user = userRef.current;
    if (!user) {
      user = await getUserFromToken();
      userRef.current = user;
      loadScouts(user);
    }

    if (!user) {
      setLoading(false);
      setFetchError("User not found");
      return;
    }

    setTeam(user.team || "");
    if (showLoading) setLoading(true);

    try {
      const res = await fetch(notesEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getSharedTeamNotes",
          team: user.team || "",
          scout: user.scout || "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false || data?.success === false) {
        throw new Error(data?.message || data?.error || "Failed to load shared notes.");
      }

      applyIncomingNotes(Array.isArray(data?.notes) ? data.notes : (data?.note ? [data.note] : []), data?.changedNoteIds);
      setFetchError(null);
    } catch (err) {
      setFetchError(err?.message || "Unable to fetch shared notes.");
    } finally {
      setLoading(false);
    }
  }, [applyIncomingNotes, loadScouts]);

  useEffect(() => {
    let alive = true;
    const poll = () => {
      if (!alive) return;
      fetchLatest({ showLoading: false });
    };

    fetchLatest({ showLoading: true });
    const intervalId = window.setInterval(poll, pollMs);
    return () => {
      alive = false;
      window.clearInterval(intervalId);
    };
  }, [fetchLatest]);

  const markNoteSeen = useCallback(async (noteId) => {
    const cleanNoteId = String(noteId || "").trim();
    const user = userRef.current;
    if (!user || !cleanNoteId) return;

    try {
      const res = await fetch(notesEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "markSharedTeamNoteSeen",
          team: user.team || "",
          scout: user.scout || "",
          id: cleanNoteId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (data?.success) {
        publishChangedCount(data?.changedNoteIds || changedNoteIds.filter((id) => String(id) !== cleanNoteId));
        setNotes((current) => current.map((item) => getNoteId(item) === cleanNoteId ? { ...item, hasUnreadChanges: false } : item));
      }
    } catch {}
  }, [changedNoteIds, notesEndpoint, publishChangedCount]);

  useEffect(() => {
    const openNoteId = getNoteId(note);
    if (!openNoteId || lastSeenNoteIdRef.current === openNoteId) return;
    lastSeenNoteIdRef.current = openNoteId;
    markNoteSeen(openNoteId);
  }, [markNoteSeen, note]);

  const chooseNote = (noteId) => {
    const nextNote = notes.find((item) => getNoteId(item) === String(noteId)) || null;
    setNotesMenuOpen(false);
    setSelectedNoteId(String(noteId));
    saveLastSharedNoteId(String(noteId));
    setNote(nextNote);
    setSaveError(null);
    markNoteSeen(String(noteId));
  };

  const renderNoteMenuItem = (item, { showCreator = false, underlineShared = false } = {}) => {
    const noteId = getNoteId(item);
    const isSelected = noteId === selectedNoteId;
    const shouldUnderline = underlineShared && noteIsSharedWithAnyone(item);
    const shareCount = shouldUnderline ? getNoteShareCount(item) : 0;
    const hasChanged = changedNoteIdSet.has(noteId) || item?.hasUnreadChanges === true || item?.hasUnreadChanges === 1 || item?.hasUnreadChanges === "1";
    return (
      <button
        type="button"
        className={`dropdown-item d-flex align-items-center justify-content-between gap-2 ${isSelected ? "active" : ""}`}
        key={noteId}
        onClick={() => chooseNote(noteId)}
        disabled={loading || isDirty}
      >
        <span className={`text-truncate ${shouldUnderline ? "text-decoration-underline" : ""}`}>
          {getNoteMenuTitle(item, showCreator)}{shareCount > 0 ? ` [ ${shareCount} ]` : ""}
        </span>
        <span className="d-flex align-items-center gap-1 flex-shrink-0">
          {hasChanged && <span className="badge bg-primary">Changed</span>}
          {isSelected && <span className="small">Open</span>}
        </span>
      </button>
    );
  };

  const toggleScout = (scoutId) => {
    setSelectedScoutIds((current) => current.includes(scoutId)
      ? current.filter((id) => id !== scoutId)
      : [...current, scoutId]);
  };

  const toggleEditShareScout = (scoutId) => {
    setEditShareScoutIds((current) => current.includes(scoutId)
      ? current.filter((id) => id !== scoutId)
      : [...current, scoutId]);
  };

  const openCreateNote = () => {
    setNewNoteTitle("");
    setShareWithEveryone(true);
    setSelectedScoutIds(scouts.map(getScoutId).filter(Boolean));
    setCreateError("");
    setShowCreateNote(true);
  };

  const createSharedNote = async () => {
    const user = userRef.current || await getUserFromToken();
    if (!user) return;

    const title = newNoteTitle.trim();
    if (!title) {
      setCreateError("Give the shared note a name.");
      return;
    }

    const sharedTo = shareWithEveryone ? [] : selectedScoutIds.filter(Boolean);

    setCreatingNote(true);
    setCreateError("");

    try {
      const res = await fetch(notesEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "createSharedTeamNote",
          team: user.team || "",
          scout: user.scout || "",
          title,
          shareScope: shareWithEveryone ? "team" : "scouts",
          sharedTo,
          user,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || "Unable to create shared note.");
      }

      const createdNote = data?.note || null;
      setShowCreateNote(false);
      setNewNoteTitle("");
      await fetchLatest({ showLoading: false });
      if (createdNote) {
        const createdNoteId = getNoteId(createdNote);
        setSelectedNoteId(createdNoteId);
        saveLastSharedNoteId(createdNoteId);
        setNote(createdNote);
      }
    } catch (err) {
      setCreateError(err?.message || "Unable to create shared note.");
    } finally {
      setCreatingNote(false);
    }
  };

  const openShareEditor = () => {
    setEditShareWithEveryone(noteSharedWithEveryone);
    setEditShareScoutIds(noteSharedScoutIds.filter((id) => id !== currentScoutId));
    setShareError("");
    setShowShareEditor(true);
  };

  const deleteSharedNote = async () => {
    if (!note || !canDeleteNote || confirmDeleteNoteId !== getNoteId(note)) return;
    const user = userRef.current || await getUserFromToken();
    if (!user) return;

    setDeletingNote(true);
    setDeleteError("");

    try {
      const deletingId = getNoteId(note);
      const res = await fetch(notesEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "deleteSharedTeamNote",
          team: user.team || "",
          scout: user.scout || "",
          id: deletingId,
          user,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || "Unable to delete shared note.");
      }

      try {
        window.localStorage.removeItem(getLastSharedNoteStorageKey(user));
      } catch {}
      setConfirmDeleteNoteId("");
      setShowShareEditor(false);
      setSelectedNoteId("");
      setNote(null);
      setNotes((current) => current.filter((item) => getNoteId(item) !== deletingId));
      await fetchLatest({ showLoading: false });
    } catch (err) {
      setDeleteError(err?.message || "Unable to delete shared note.");
    } finally {
      setDeletingNote(false);
    }
  };

  const saveShareSettings = async () => {
    if (!note || isMainNote) return;
    const user = userRef.current || await getUserFromToken();
    if (!user) return;

    setSavingShare(true);
    setShareError("");

    try {
      const res = await fetch(notesEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "updateSharedTeamNoteSharing",
          team: user.team || "",
          scout: user.scout || "",
          id: getNoteId(note),
          shareScope: editShareWithEveryone ? "team" : "scouts",
          sharedTo: editShareWithEveryone ? [] : editShareScoutIds,
          user,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || "Unable to update sharing.");
      }

      const updatedNote = data?.note || {
        ...note,
        shareScope: editShareWithEveryone ? "team" : "scouts",
        sharedTo: editShareWithEveryone ? "" : [...editShareScoutIds, currentScoutId].filter(Boolean).join(","),
      };
      setNote(updatedNote);
      setNotes((current) => current.map((item) => getNoteId(item) === getNoteId(updatedNote) ? updatedNote : item));
      setShowShareEditor(false);
      await fetchLatest({ showLoading: false });
    } catch (err) {
      setShareError(err?.message || "Unable to update sharing.");
    } finally {
      setSavingShare(false);
    }
  };

  return (
    <DragBox2026
      storageKey="SHARED_NOTE_POSITION"
      defaultPos={{ x: 58, y: 95 }}
      title=""
      minWidth={1400}
      maxWidth={1400}
      zIndex={500}
      showReset={true}
    >
      <div className="card shadow-sm" style={{ borderRadius: 16, overflow: "hidden" }}>
        <div className="card-header d-flex align-items-center gap-2">
          <div className="d-flex align-items-center gap-2 flex-shrink-0">
            <div className="dropdown" ref={notesMenuRef} style={{ width: 320 }}>
              <button
                type="button"
                className="btn btn-sm btn-light border dropdown-toggle w-100 d-flex align-items-center justify-content-between gap-2"
                onClick={() => setNotesMenuOpen((open) => !open)}
                disabled={loading || isDirty}
                title={isDirty ? "Finish autosave before switching notes" : "Choose shared note"}
              >
                <span className="text-truncate">{selectedNoteTitle}</span>
              </button>
              {notesMenuOpen && (
                <div className="dropdown-menu show p-2 shadow" style={{ width: 360, maxHeight: 360, overflowY: "auto" }}>
                  {defaultSharedNote ? (
                    renderNoteMenuItem(defaultSharedNote)
                  ) : (
                    <div className="dropdown-item-text text-muted small">No default Shared Note found.</div>
                  )}
                  <div className="dropdown-divider" />
                  <div className="dropdown-header px-2">My Notes</div>
                  {myNotes.length > 0 ? (
                    myNotes.map((item) => renderNoteMenuItem(item, { underlineShared: true }))
                  ) : (
                    <div className="dropdown-item-text text-muted small">No notes created by you.</div>
                  )}
                  <div className="dropdown-divider" />
                  <div className="dropdown-header px-2">Notes Shared with Me</div>
                  {sharedNotes.length > 0 ? (
                    sharedNotes.map((item) => renderNoteMenuItem(item, { showCreator: true }))
                  ) : (
                    <div className="dropdown-item-text text-muted small">No notes shared with you.</div>
                  )}
                </div>
              )}
            </div>
            <button type="button" className="btn btn-sm btn-outline-primary" onClick={openCreateNote}>
              Create New Note
            </button>
          </div>
          <div
            style={{
              border: "1px solid #c4ccd6",
              borderRadius: 4,
              background: "#f8fafc",
              color: "#2f3742",
              fontSize: 11,
              fontWeight: 500,
              lineHeight: "14px",
              minHeight: 28,
              minWidth: 250,
              padding: "6px 8px",
              whiteSpace: "nowrap",
            }}
          >
            Last saved by {lastSavedBy}{lastSavedAt ? ` - ${lastSavedAt}` : ""}
          </div>
          {changedNoteCount > 0 && (
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                border: "1px solid #d6a400",
                borderRadius: 4,
                background: "#fff4bd",
                color: "#553f00",
                fontSize: 11,
                fontWeight: 700,
                minHeight: 28,
                minWidth: 132,
                padding: "4px 8px",
                whiteSpace: "nowrap",
              }}
              title="Notes updated since you last opened them"
            >
              Updated notes: {changedNoteCount}
            </div>
          )}
          <div className="flex-grow-1" style={{ minWidth: 0 }} />
          <div
            className="d-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              border: "1px solid #c4ccd6",
              borderRadius: 4,
              background: "#fff",
              color: "#2f3742",
              fontSize: 11,
              fontWeight: 700,
              minHeight: 28,
              minWidth: 124,
              padding: "4px 8px",
            }}
          >
            {refreshRequired && <span>You must refresh</span>}
            {!refreshRequired && isDirty && <span>Autosaving...</span>}
            {!refreshRequired && !isDirty && loading && <span>Updating...</span>}
            {!refreshRequired && !isDirty && !loading && <span>Saved</span>}
          </div>
        </div>

        <div className="card-body">
          {showCreateNote && (
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <strong>Create Shared Note</strong>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowCreateNote(false)} disabled={creatingNote}>
                  Close
                </button>
              </div>
              <div className="row g-2 align-items-end">
                <div className="col-16">
                  <label className="form-label small fw-bold">Name</label>
                  <input
                    className="form-control form-control-sm"
                    value={newNoteTitle}
                    onChange={(event) => setNewNoteTitle(event.target.value)}
                    placeholder="Shared note name"
                  />
                </div>
                <div className="col-16">
                  <label className="form-label small fw-bold">Share With</label>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="shared-note-share-everyone"
                      checked={shareWithEveryone}
                      onChange={(event) => {
                        const nextShareWithEveryone = event.target.checked;
                        setShareWithEveryone(nextShareWithEveryone);
                        setSelectedScoutIds(nextShareWithEveryone ? scouts.map(getScoutId).filter(Boolean) : []);
                      }}
                    />
                    <label className="form-check-label" htmlFor="shared-note-share-everyone">
                      Everyone on {team}
                    </label>
                  </div>
                </div>
                <div className="col-8">
                  <button type="button" className="btn btn-sm btn-primary w-100" onClick={createSharedNote} disabled={creatingNote}>
                    {creatingNote ? "Creating..." : "Create"}
                  </button>
                </div>
              </div>
              {!shareWithEveryone && (
                <div className="mt-3">
                  <div className="small fw-bold mb-2">Scouts on my team</div>
                  <div className="d-flex flex-wrap gap-2">
                    {otherScouts.map((scout) => {
                      const scoutId = getScoutId(scout);
                      if (!scoutId) return null;
                      return (
                        <label className="btn btn-sm btn-outline-secondary" key={scoutId}>
                          <input
                            type="checkbox"
                            className="form-check-input me-1"
                            checked={selectedScoutIds.includes(scoutId)}
                            onChange={() => toggleScout(scoutId)}
                          />
                          {getScoutName(scout)}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              {createError && <div className="alert alert-danger py-2 mt-3 mb-0">{createError}</div>}
            </div>
          )}

          {showShareEditor && note && !isMainNote && (
            <div className="border rounded p-3 mb-3 bg-light">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <strong>Sharing: {note?.title || "Shared Note"}</strong>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowShareEditor(false)} disabled={savingShare}>
                  Close
                </button>
              </div>
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="shared-note-edit-share-everyone"
                  checked={editShareWithEveryone}
                  onChange={(event) => {
                    const nextValue = event.target.checked;
                    setEditShareWithEveryone(nextValue);
                    if (!nextValue) setEditShareScoutIds([]);
                  }}
                />
                <label className="form-check-label" htmlFor="shared-note-edit-share-everyone">
                  Everyone on {team}
                </label>
              </div>
              {!editShareWithEveryone && (
                <div className="mt-2">
                  <div className="small fw-bold mb-2">Scouts who can see this note</div>
                  <div className="d-flex flex-wrap gap-2">
                    {otherScouts.map((scout) => {
                      const scoutId = getScoutId(scout);
                      if (!scoutId) return null;
                      return (
                        <label className="btn btn-sm btn-outline-secondary" key={scoutId}>
                          <input
                            type="checkbox"
                            className="form-check-input me-1"
                            checked={editShareScoutIds.includes(scoutId)}
                            onChange={() => toggleEditShareScout(scoutId)}
                          />
                          {getScoutName(scout)}
                        </label>
                      );
                    })}
                  </div>
                  <div className="text-muted small mt-2">You will always keep access to notes you edit.</div>
                </div>
              )}
              <div className="d-flex justify-content-end gap-2 mt-3">
                <button type="button" className="btn btn-sm btn-primary" onClick={saveShareSettings} disabled={savingShare}>
                  {savingShare ? "Saving..." : "Save Sharing"}
                </button>
              </div>
              {shareError && <div className="alert alert-danger py-2 mt-3 mb-0">{shareError}</div>}
            </div>
          )}

          {confirmDeleteNoteId && note && confirmDeleteNoteId === getNoteId(note) && canDeleteNote && (
            <div className="alert alert-danger d-flex align-items-center justify-content-between gap-3 py-2">
              <div>
                <strong>Delete shared note?</strong> This cannot be undone.
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-danger" onClick={deleteSharedNote} disabled={deletingNote}>
                  {deletingNote ? "Deleting..." : "Yes, Delete"}
                </button>
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setConfirmDeleteNoteId("")} disabled={deletingNote}>
                  Cancel
                </button>
              </div>
            </div>
          )}
          {deleteError && <div className="alert alert-danger py-2 mb-3">{deleteError}</div>}

          {loading && <div className="text-muted small">Loading the latest note...</div>}
          {!loading && fetchError && <div className="alert alert-danger mb-3">{fetchError}</div>}
          {!loading && !fetchError && !note && (
            <div className="alert alert-info mb-0">
              No shared notes were found for team <strong>{team}</strong>.
            </div>
          )}
          {!loading && !fetchError && note && (
            <>
              <div
                className="d-flex align-items-center gap-2 mb-2"
                style={{
                  background: "#e5e7eb",
                  border: "1px solid #cbd2dc",
                  borderRadius: 4,
                  minHeight: 34,
                  padding: "5px 8px",
                }}
              >
                <div className="d-flex align-items-center gap-1 flex-wrap flex-grow-1" style={{ minWidth: 0 }}>
                  <span style={{ color: "#2f3742", fontSize: 11, fontWeight: 800, marginRight: 4 }}>
                    Shared To:
                  </span>
                  {getSharedPillLabels().map((label) => (
                    <span
                      className="badge rounded-pill"
                      key={label}
                      style={{
                        background: "#fff",
                        border: "1px solid #b9c2cf",
                        color: "#2f3742",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {label}
                    </span>
                  ))}
                  {!noteSharedWithEveryone && getSharedPillLabels().length === 0 && (
                    <span
                      className="badge rounded-pill"
                      style={{
                        background: "#198754",
                        border: "1px solid #146c43",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 800,
                      }}
                    >
                      Not shared
                    </span>
                  )}
                </div>
                {canEditNoteSharing && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={openShareEditor}>
                    Edit
                  </button>
                )}
                {canDeleteNote && (
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setConfirmDeleteNoteId(getNoteId(note))}>
                    Delete
                  </button>
                )}
              </div>
              <SharedNoteEditor
                key={getNoteId(note)}
                note={note}
                onDirtyChange={setIsDirty}
                onRefreshRequiredChange={setRefreshRequired}
                onSaved={(savedNote) => {
                  if (savedNote) {
                    saveLastSharedNoteId(getNoteId(savedNote));
                    setNote(savedNote);
                    setNotes((current) => current.map((item) => getNoteId(item) === getNoteId(savedNote) ? savedNote : item));
                    markNoteSeen(getNoteId(savedNote));
                  }
                  setSaveError(null);
                }}
                onSaveError={setSaveError}
              />
              {saveError && <div className="alert alert-danger mt-3 mb-0">{saveError}</div>}
            </>
          )}
        </div>
      </div>
    </DragBox2026>
  );
};

export default SharedNotes;
